import {
    Button,
    Card,
    CardContent,
    CardHeader,
    colors,
    Grid,
    InputAdornment,
    Link,
    makeStyles,
    Switch,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import React, {ChangeEvent, useEffect, useState} from 'react';
import useRouter from 'use-react-router';
import validate from 'validate.js';
import {Can} from '../../../components/Can';
import ConfirmDelete from '../../../components/ConfirmDelete';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import anyLoading from '../../../utils/anyLoading';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import {WcvDocument} from '../../../graphql/documents.datatypes';
import createDocument, {CreateDocumentInput} from '../createDocument.api';
import updateDocument, {UpdateDocumentInput} from '../updateDocument.api';
import deleteDocument from './deleteDocument.api';
import uploadFile, {UploadFileVars} from '../../../graphql/uploadFile.api';
import {getHost} from '../../../graphql/GraphQlProvider';
import {WCVDatePicker} from '../../../components/DateUtilsProvider/WCVDatePicker';
import {formatGraphQlDate} from '../../../utils/formatters';
import {TourRoleTypes} from '../../../graphql/groups.datatypes';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        whiteSpace: 'nowrap',
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        '&:hover': {
            backgroundColor: theme.palette.error.main
        }
    },
    errorIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.error.main,
    },
    marginBottom: {
        paddingLeft: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    linkButton: {
        marginRight: theme.spacing(1),
    },
    tabs: {},
}));


export const DocumentEdit = (props: { document: WcvDocument, setSelectedDocument?: (document: WcvDocument | undefined) => void, fileTypes?: string, }) => {
    const classes = useStyles();
    const {document, setSelectedDocument, fileTypes} = props;
    const isNewDocument = document.id === -1;
    const {history} = useRouter();
    const {enqueueSnackbar} = useSnackbar();
    const [sendUploadFile, uploadFileData] = uploadFile();
    const [sendCreateDocument, createData] = createDocument();
    const [sendUpdateDocument, updateData] = updateDocument();
    const [sendDeleteDocument, deleteData] = deleteDocument();

    //1. Set form and error state
    const [uploadFileInput, setUploadFileInput] = useState<UploadFileVars | undefined>();
    const [formState, setFormState] = useState({...document});
    const loadingData = anyLoading(createData, updateData, deleteData, uploadFileData);
    const [errors, setErrors] = useState({
        isValid: false,
        noFile: true,
        errors: {}
    });

    useEffect(() => {
        setFormState({...document});
    }, [document]);

    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        title: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        description: {
            presence: {allowEmpty: false, message: 'is required'},
        },
    };

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);

        setErrors(state => ({
            ...state,
            noFile: (isNewDocument && !uploadFileInput),
            isValid: !newErrors && !(isNewDocument && !uploadFileInput),
            errors: newErrors || {}
        }));
    }, [formState, uploadFileInput]);

    //4. Handle save form
    const onSaveClick = () => {
        if (isNewDocument) {
            if (uploadFileInput) {
                sendUploadFile({variables: uploadFileInput})
                    .then((response) => {
                        enqueueSnackbar('File Upload Success!', {variant: 'success'});
                        //setFormState({...formState, [attributeName]: response.data!.uploadFile});

                        const sanitisedData: CreateDocumentInput = {
                            data: {
                                ...formState,
                                url: response.data!.uploadFile,
                                __typename: undefined,
                                id: undefined,
                                completion_date: formatGraphQlDate(formState.completion_date),
                                completed_documents: undefined,
                                overdueGroupMembers: undefined,
                            }
                        };

                        sendCreateDocument({variables: sanitisedData})
                            .then(() => {
                                enqueueSnackbar('Document Saved.', {variant: 'success'});
                                if (setSelectedDocument) {
                                    setSelectedDocument(undefined);
                                } else {
                                    history.push('/documents');
                                }
                            })
                            .catch(() => {
                                enqueueSnackbar('Save Document Failed!', {variant: 'error'});
                            });
                    })
                    .catch(() => {
                        enqueueSnackbar('File Upload Failed!', {variant: 'error'});
                    });
            }
        } else {

            const sanitisedData: UpdateDocumentInput = {
                data: {
                    ...formState,
                    __typename: undefined,
                    completion_date: formatGraphQlDate(formState.completion_date),
                    url: undefined,
                    user: undefined,
                    targetResource: undefined,
                    targetGroup: undefined,
                    targetGroupMember: undefined,
                    completed_documents: undefined,
                    overdueGroupMembers: undefined,
                }
            };

            if (uploadFileInput) {
                sendUploadFile({variables: uploadFileInput})
                    .then((response) => {
                        enqueueSnackbar('File Upload Success!', {variant: 'success'});
                        //setFormState({...formState, [attributeName]: response.data!.uploadFile});
                        sanitisedData.data.url = response.data!.uploadFile;

                        sendUpdateDocument({variables: sanitisedData})
                            .then(() => {
                                enqueueSnackbar('Document Updated.', {variant: 'success'});
                                if (setSelectedDocument) setSelectedDocument(undefined);
                            })
                            .catch(() => {
                                enqueueSnackbar('Document Update Failed!', {variant: 'error'});
                            });
                    })
                    .catch(() => {
                        enqueueSnackbar('File Upload Failed!', {variant: 'error'});
                    });
            } else {
                sendUpdateDocument({variables: sanitisedData})
                    .then(() => {
                        enqueueSnackbar('Document Updated.', {variant: 'success'});
                        if (setSelectedDocument) setSelectedDocument(undefined);
                    })
                    .catch(() => {
                        enqueueSnackbar('Document Update Failed!', {variant: 'error'});
                    });
            }

        }
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            if (file.size <= 5000000) {
                const sanitisedData: UploadFileVars = {
                    data: {
                        file,
                    },
                };
                setUploadFileInput(sanitisedData);
                setFormState({...formState, url: 'file selected ' + event.target.files[0].name})
            } else {
                enqueueSnackbar('Document Too Large. 5MB limit.', {variant: 'error'});
            }
        }
    };

    //Delete modal functions
    const [open, setOpen] = useState(false);
    const handleClickOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleDelete = () => {
        sendDeleteDocument({variables: {id: formState.id}})
            .then(() => {
                enqueueSnackbar('Document deleted.', {variant: 'success'});
                if (setSelectedDocument) {
                    setSelectedDocument(undefined);
                } else {
                    history.push('/documents');
                }
            })
            .catch(() => {
                enqueueSnackbar('Deleted Document Failed!', {variant: 'error'});
            });

        handleClose();
    };

    return (
        <form>
            <LoadingOverlay loading={loadingData.loading}>

                <Card className={classes.container}>
                    <CardHeader title="Document Details" action={[
                        !isNewDocument && formState.targetGroup &&
                        <Button key={1} variant="contained" className={classes.linkButton}
                                onClick={() => history.push('/groups/' + formState.targetGroup!.id)}>Go to
                            Group</Button>,
                        !isNewDocument && formState.targetGroupMember &&
                        <Button key={2} variant="contained" className={classes.linkButton}
                                onClick={() => history.push('/groupmembers/' + formState.targetGroupMember!.id)}>Go to
                            Group Member</Button>,
                        !isNewDocument && formState.targetResource &&
                        <Button key={3} variant="contained" className={classes.linkButton}
                                onClick={() => history.push('/resources')}>Go to Tour</Button>,
                    ]}/>
                    <CardContent className={classes.card}>
                        <Grid container spacing={3}>

                            <Grid item xs={12}>
                                <TextField
                                    {...hasError('title')}
                                    fullWidth
                                    label="title"
                                    name="title"
                                    onChange={handleFieldChange}
                                    value={formState.title}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    {...hasError('description')}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    rowsMax={3}
                                    label="description"
                                    name="description"
                                    onChange={handleFieldChange}
                                    value={formState.description}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    {...hasError('url')}
                                    error={errors.noFile}
                                    helperText={errors.noFile ? 'File upload is required. Max size 5MB.' : 'Max size 5MB'}
                                    fullWidth
                                    label="url"
                                    name="url"
                                    onChange={handleFieldChange}
                                    value={formState.url}
                                    variant="outlined"
                                    disabled={true}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <label htmlFor="file_upload">
                                                    <Button variant="contained" component="span"
                                                            className={classes.saveButton}>
                                                        Choose File
                                                    </Button>
                                                </label>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <input
                                    accept={fileTypes ? fileTypes : 'application/pdf,image/*'}
                                    style={{display: 'none'}}
                                    id="file_upload"
                                    multiple
                                    type="file"
                                    onChange={event => handleFileChange(event)}
                                    value={''}
                                />
                            </Grid>


                            <Grid item  xs={12}>
                                <TextField
                                    {...hasError('tour_role')}
                                    fullWidth
                                    label="Tour Role"
                                    name="tour_role"
                                    onChange={handleFieldChange}
                                    select
                                    // eslint-disable-next-line react/jsx-sort-props
                                    SelectProps={{native: true}}
                                    value={formState.tour_role}
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                >
                                    <option key={-1} value={-1}>All Roles</option>
                                    {TourRoleTypes.map((type, i) => (
                                        <option key={i} value={i}>
                                            {TourRoleTypes[i].name}
                                        </option>
                                    ))}
                                </TextField>
                            </Grid>

                            {document.url && document.url !== '' && (
                                <Grid item xs={12}>
                                    <Link component={Button} href={getHost() + document.url} target="_blank"
                                          className={classes.saveButton}>
                                        View File
                                    </Link>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Grid component="label" container alignItems="center" spacing={1}>
                                    <Grid item>
                                        <Switch
                                            checked={formState.has_completion_date}
                                            color="secondary"
                                            edge="start"
                                            name="has_completion_date"
                                            onChange={handleFieldChange}
                                            value={formState.has_completion_date}
                                        />
                                    </Grid>
                                    <Grid item><Typography variant="h5">Has Completion Date?</Typography></Grid>
                                </Grid>
                            </Grid>

                            {formState.has_completion_date && (
                                <Grid item xs={12}>
                                    <WCVDatePicker
                                        {...hasError('completion_date')}
                                        id="completion_date"
                                        label="Complete By:"
                                        value={formState.completion_date}
                                        onChange={(date, value) => {
                                            setFormState({
                                                ...formState,
                                                completion_date: value ? value : ''
                                            })
                                        }}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Grid component="label" container alignItems="center" spacing={1}>
                                    <Grid item>
                                        <Switch
                                            checked={formState.available_for_download}
                                            color="secondary"
                                            edge="start"
                                            name="available_for_download"
                                            onChange={handleFieldChange}
                                            value={formState.available_for_download}
                                        />
                                    </Grid>
                                    <Grid item><Typography variant="h5">For Download?</Typography></Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid component="label" container alignItems="center" spacing={1}>
                                    <Grid item>
                                        <Switch
                                            checked={formState.verified}
                                            color="secondary"
                                            edge="start"
                                            name="verified"
                                            onChange={handleFieldChange}
                                            value={formState.verified}
                                        />
                                    </Grid>
                                    <Grid item><Typography variant="h5">Verified?</Typography></Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Button className={classes.saveButton} onClick={onSaveClick} variant="contained"
                                        disabled={!errors.isValid}>Save</Button>

                                <Can permission="documents.delete">
                                    <Button onClick={handleClickOpen} className={classes.deleteButton}
                                            variant="contained" disabled={isNewDocument}>
                                        Delete
                                    </Button>
                                </Can>

                            </Grid>

                        </Grid>
                    </CardContent>
                </Card>

            </LoadingOverlay>

            <ConfirmDelete onClose={handleClose} onDeleteClick={handleDelete} open={open}/>
        </form>
    );
};

export default DocumentEdit;